import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type { GridPaginationModel, GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { supabase } from "../lib/supabase";
import type { PersonInput, PersonRow } from "../schemas/person";

export const personKeys = {
  all: ["persons"] as const,
  lists: () => [...personKeys.all, "list"] as const,
  list: (params: ListPersonsParams) => [...personKeys.lists(), params] as const,
  details: () => [...personKeys.all, "detail"] as const,
  detail: (id: string) => [...personKeys.details(), id] as const,
};

export interface ListPersonsParams {
  pagination: GridPaginationModel;
  sort: GridSortModel;
  filter: GridFilterModel;
}

// Server-side list — pagination, sorting, filtering pushed to Postgres.
// Never load all persons into the browser.
export function usePersonsList(params: ListPersonsParams) {
  return useQuery({
    queryKey: personKeys.list(params),
    queryFn: async () => {
      const { pagination, sort, filter } = params;
      const from = pagination.page * pagination.pageSize;
      const to = from + pagination.pageSize - 1;

      let query = supabase
        .from("manna_persons_view") // exposes manna.persons view
        .select("*", { count: "exact" })
        .range(from, to);

      // Sort
      if (sort.length > 0) {
        const s = sort[0]!;
        query = query.order(s.field, { ascending: s.sort === "asc" });
      } else {
        query = query.order("created_at", { ascending: false });
      }

      // Filter — translate MUI grid filter items to Supabase query
      for (const item of filter.items) {
        if (!item.value) continue;
        switch (item.operator) {
          case "contains":
            query = query.ilike(item.field, `%${item.value}%`);
            break;
          case "equals":
            query = query.eq(item.field, item.value);
            break;
          case "startsWith":
            query = query.ilike(item.field, `${item.value}%`);
            break;
        }
      }

      const { data, count, error } = await query;
      if (error) throw error;
      return { rows: data ?? [], rowCount: count ?? 0 };
    },
    placeholderData: keepPreviousData,
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({
    queryKey: personKeys.detail(id ?? ""),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manna_persons_view")
        .select("*")
        .eq("id", id!)
        .maybeSingle();
      if (error) throw error;
      // SPEC manna-web-ui §3.13: when RLS hides a row, surface "not found"
      // — never disclose that a record exists but is gated.
      if (!data) throw new Error("Contact not found.");
      return data as PersonRow;
    },
    enabled: Boolean(id),
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PersonInput) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await supabase.rpc("manna_create_person", { payload: input } as any);
      if (error) throw error;
      return data;
    },
    // Same-session post-insert read primes the React Query cache so the detail
    // page mounts on cache hit. Avoids a redundant fetch and a brief flash.
    onSuccess: async (id) => {
      qc.invalidateQueries({ queryKey: personKeys.lists() });
      if (typeof id === "string" && id) {
        const { data } = await supabase
          .from("manna_persons_view")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (data) qc.setQueryData(personKeys.detail(id), data);
      }
    },
  });
}

// Optimistic concurrency: callers pass expected row_version.
// Supabase RPC checks version; mismatch returns a conflict error we surface to the UI.
export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PersonInput & { id: string; row_version: number }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await supabase.rpc("manna_update_person", { payload: input } as any);
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: personKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: personKeys.lists() });
    },
  });
}
